import { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import "./index.css";
import Cookies from "js-cookie";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "./redux/store";
import paths from "./navigation/paths";
import LoginPage from "./features/auth/login";
import BlankLayout from "./core/layouts/blank-layout";
import {
  adminCat,
  cookieName,
  mgTeam,
  seniorTeam,
} from "./core/utils/constants";
import themeConfig from "./core/configs/theme-config";
import DailyReportPage from "./features/daily-report";
import UserLayout from "./core/layouts/user-layout";
import { getUserState, getUserStateAction } from "./redux/features/auth-slice";
import UnauthorizedPage from "./features/auth/unauthorized";
import WeeklyReportsPage from "./features/weekly-report";
import { UserRole } from "./redux/features/user-slice";
import TeamsPage from "./features/teams";
import TeamMemberPage from "./features/teams/member";
import ChangePasswordPage from "./features/settings/change-password";
import MyFilesPage from "./features/documents/my-files";
import DocsSharedPage from "./features/documents/shared";
import ViewerPage from "./features/viewer";
import TasksPage from "./features/task-management/tasks";
import RecurrencesPage from "./features/task-management/recurrences";
import ErrorBoundary from "./error-boundary";
import NotFoundPage from "./features/not-found";
import TeamLeaderPage from "./features/teams/leader";
import DesignDailyReportPage from "./features/design-daily-report";
import DesignWeeklyReportsPage from "./features/design-weekly-report";
import DesignMemberPage from "./features/teams/design-member";
import JobTypesPage from "./features/task-management/job-types";
import RelationshipStatusPage from "./features/task-management/relationship-status";
import RequestTypesPage from "./features/task-management/request-types";
import UsersPage from "./features/users-management/list";
import UserDetailPage from "./features/users-management/detail";
import SettingsLayout from "./core/layouts/settings-layout";
import ProfilePage from "./features/settings/profile";
import AddUserPage from "./features/users-management/add-user";
import SupportResourcePage from "./features/settings/summary-properties/support-resources";
import ReasonsPage from "./features/settings/summary-properties/reasons";
import ChallengesPage from "./features/settings/summary-properties/challenges";
import IssuesPage from "./features/settings/summary-properties/issues";
import ExportReportsPage from "./features/data/export-reports";
import GroupTeamsPage from "./features/group-team";
import AddScheduleReportPage from "./features/data/schedule-reports/add-schedule-report";
import ScheduleReportsPage from "./features/data/schedule-reports/list";
import ScheduleDetailPage from "./features/data/schedule-reports/detail";
import WorkCalendar from "./features/calendar";
import IncidentsListPage from "./features/incident";
import CreateIncidentPage from "./features/incident/create-incident";
import UpdateIncidentPage from "./features/incident/detail";
import IncidentDashboardPage from "./features/incident/dashboard";
import AddCoursePage from "./features/course/add";
import CoursesPage from "./features/course";
import UpdateCoursePage from "./features/course/detail";
import UserCoursesPage from "./features/course/list-for-user";
import UserCourseDetailPage from "./features/course/detail-for-user";
import AddCustomerPage from "./features/customer/add";
import CustomersPage from "./features/customer";
import UpdateCustomerPage from "./features/customer/detail";
import AddJDPage from "./features/jd/add";
import JDListPage from "./features/jd";
import UpdateJobTypeDialog from "./features/task-management/job-types/components/update-job-type-dialog";
import JDDetailPage from "./features/jd/detail";
import AddCandidatePage from "./features/candidate/add";
import CandidatesPage from "./features/candidate";
import UpdateCandidatePage from "./features/candidate/detail";

export default function App() {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;

  const checkNavigateForAdmin = () => {
    if (user !== null) {
      if ([UserRole.Admin, UserRole.Supervisor].includes(user.role)) {
        if (user.category.id === adminCat.id) {
          return `${paths.CATEGORIES}/${adminCat.name}/${adminCat.id}`;
        } else {
          return `${paths.CATEGORIES}/${user.category.title}/${user.category.id}`;
        }
      } else {
        return paths.COURSES_USER;
      }
    }
    return paths.LOGIN;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      await dispatch(getUserStateAction());
    };
    if (Cookies.get(cookieName) !== undefined) {
      fetchUserData();
    }
  }, [dispatch]);

  return (
    <HelmetProvider>
      <Helmet
        titleTemplate={`%s • ${themeConfig.appName}`}
        defaultTitle={themeConfig.appName}
      />
      <ErrorBoundary>
        <RouterProvider
          router={createBrowserRouter([
            {
              path: paths.DEFAULT,
              element: <Navigate to={checkNavigateForAdmin()} />,
            },
            {
              path: paths.LOGIN,
              element: (
                <BlankLayout>
                  <LoginPage />
                </BlankLayout>
              ),
            },
            {
              path: paths.DAILY_REPORTS,
              element: (
                <UserLayout>
                  <UserCoursesPage />
                </UserLayout>
              ),
            },
            {
              path: paths.WEEKLY_REPORTS,
              element: (
                <UserLayout>
                  <WeeklyReportsPage />
                </UserLayout>
              ),
            },
            {
              path: paths.CATEGORIES,
              element: (
                <UserLayout>
                  <TeamLeaderPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.CATEGORIES}/:name/:id`,
              element: (
                <UserLayout>
                  <TeamsPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.GROUP_TEAMS}/:name/:id`,
              element: (
                <UserLayout>
                  <GroupTeamsPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.MEMBER}/:id/:teamId`,
              element: (
                <UserLayout>
                  <TeamMemberPage />
                </UserLayout>
              ),
            },
            // {
            //   path: `${paths.DESIGN_MEMBER}/:id/:teamId`,
            //   element: (
            //     <UserLayout>
            //       <DesignMemberPage />
            //     </UserLayout>
            //   ),
            // },
            // {
            //   path: paths.TASKS,
            //   element: (
            //     <UserLayout>
            //       <TasksPage />
            //     </UserLayout>
            //   ),
            // },
            // {
            //   path: `${paths.TASKS}/:name/:id`,
            //   element: (
            //     <UserLayout>
            //       <TasksPage />
            //     </UserLayout>
            //   ),
            // },
            {
              path: paths.PROFILE,
              element: (
                <SettingsLayout>
                  <ProfilePage />
                </SettingsLayout>
              ),
            },
            {
              path: paths.CHANGE_PASSWORD,
              element: (
                <BlankLayout>
                  <ChangePasswordPage />
                </BlankLayout>
              ),
            },
            // {
            //   path: paths.RECURRENCES,
            //   element: (
            //     <SettingsLayout>
            //       <RecurrencesPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: `${paths.RECURRENCES}/:name/:id`,
            //   element: (
            //     <SettingsLayout>
            //       <RecurrencesPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: paths.JOB_TYPES,
            //   element: (
            //     <SettingsLayout>
            //       <JobTypesPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: `${paths.JOB_TYPES}/:name/:id`,
            //   element: (
            //     <SettingsLayout>
            //       <JobTypesPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: paths.RELATIONSHIP_STATUS,
            //   element: (
            //     <SettingsLayout>
            //       <RelationshipStatusPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: `${paths.RELATIONSHIP_STATUS}/:name/:id`,
            //   element: (
            //     <SettingsLayout>
            //       <RelationshipStatusPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: paths.REQUEST_TYPES,
            //   element: (
            //     <SettingsLayout>
            //       <RequestTypesPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: `${paths.REQUEST_TYPES}/:name/:id`,
            //   element: (
            //     <SettingsLayout>
            //       <RequestTypesPage />
            //     </SettingsLayout>
            //   ),
            // },
            // {
            //   path: paths.MY_FILES,
            //   element: (
            //     <UserLayout>
            //       <MyFilesPage />
            //     </UserLayout>
            //   ),
            // },
            // {
            //   path: `${paths.MY_FILES}/:id`,
            //   element: (
            //     <UserLayout>
            //       <MyFilesPage />
            //     </UserLayout>
            //   ),
            // },
            // {
            //   path: paths.VIEWER,
            //   element: (
            //     <BlankLayout>
            //       <ViewerPage />
            //     </BlankLayout>
            //   ),
            // },
            // {
            //   path: paths.SHARED,
            //   element: (
            //     <UserLayout>
            //       <DocsSharedPage />
            //     </UserLayout>
            //   ),
            // },
            {
              path: paths.USERS,
              element: (
                <UserLayout>
                  <UsersPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.USERS}/:id`,
              element: (
                <UserLayout>
                  <UserDetailPage />
                </UserLayout>
              ),
            },
            {
              path: paths.ADD_USER,
              element: (
                <UserLayout>
                  <AddUserPage />
                </UserLayout>
              ),
            },
            {
              path: paths.EXPORT_REPORTS,
              element: (
                <UserLayout>
                  <ExportReportsPage />
                </UserLayout>
              ),
            },
            {
              path: paths.ADD_SCHEDULE_REPORT,
              element: (
                <UserLayout>
                  <AddScheduleReportPage />
                </UserLayout>
              ),
            },
            {
              path: paths.SCHEDULE_REPORTS,
              element: (
                <UserLayout>
                  <ScheduleReportsPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.SCHEDULE_REPORTS}/:id`,
              element: (
                <UserLayout>
                  <ScheduleDetailPage />
                </UserLayout>
              ),
            },
            {
              path: paths.WORK_CALENDAR,
              element: (
                <UserLayout>
                  <WorkCalendar />
                </UserLayout>
              ),
            },
            {
              path: `${paths.ISSUES}/:name/:id`,
              element: (
                <SettingsLayout>
                  <IssuesPage />
                </SettingsLayout>
              ),
            },
            {
              path: `${paths.SUPPORT_RESOURCES}/:name/:id`,
              element: (
                <SettingsLayout>
                  <SupportResourcePage />
                </SettingsLayout>
              ),
            },
            {
              path: `${paths.REASONS}/:name/:id`,
              element: (
                <SettingsLayout>
                  <ReasonsPage />
                </SettingsLayout>
              ),
            },
            {
              path: `${paths.CHALLENGES}/:name/:id`,
              element: (
                <SettingsLayout>
                  <ChallengesPage />
                </SettingsLayout>
              ),
            },
            {
              path: paths.UNAUTHORIZED,
              element: (
                <BlankLayout>
                  <UnauthorizedPage />
                </BlankLayout>
              ),
            },
            {
              path: paths.INCIDENTS,
              element: (
                <UserLayout>
                  <IncidentsListPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.INCIDENTS}/:id`,
              element: (
                <UserLayout>
                  <UpdateIncidentPage />
                </UserLayout>
              ),
            },
            {
              path: paths.ADD_INCIDENT,
              element: (
                <UserLayout>
                  <CreateIncidentPage />
                </UserLayout>
              ),
            },
            {
              path: paths.INCIDENTS_DASHBOARD,
              element: (
                <UserLayout>
                  <IncidentDashboardPage />
                </UserLayout>
              ),
            },
            {
              path: paths.ADD_COURSE,
              element: (
                <UserLayout>
                  <AddCoursePage />
                </UserLayout>
              ),
            },
            {
              path: paths.COURSES,
              element: (
                <UserLayout>
                  <CoursesPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.COURSES}/:id`,
              element: (
                <UserLayout>
                  <UpdateCoursePage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.COURSES_USER}`,
              element: (
                <UserLayout>
                  <UserCoursesPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.COURSES_USER}/:id`,
              element: (
                <UserLayout>
                  <UserCourseDetailPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.ADD_CUSTOMER}`,
              element: (
                <UserLayout>
                  <AddCustomerPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.CUSTOMERS}`,
              element: (
                <UserLayout>
                  <CustomersPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.CUSTOMERS}/:id`,
              element: (
                <UserLayout>
                  <UpdateCustomerPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.ADD_JD}`,
              element: (
                <UserLayout>
                  <AddJDPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.JDS}`,
              element: (
                <UserLayout>
                  <JDListPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.JDS}/:id`,
              element: (
                <UserLayout>
                  <JDDetailPage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.ADD_CANDIDATE}`,
              element: (
                <UserLayout>
                  <AddCandidatePage />
                </UserLayout>
              ),
            },
            {
              path: `${paths.CANDIDATES}`,
              element: (
                <UserLayout>
                  <CandidatesPage />
                </UserLayout>
              ),
            },
             {
              path: `${paths.CANDIDATES}/:id`,
              element: (
                <UserLayout>
                  <UpdateCandidatePage />
                </UserLayout>
              ),
            },
            {
              path: "*",
              element: <NotFoundPage />,
            },
          ])}
        />
      </ErrorBoundary>
    </HelmetProvider>
  );
}
